import { CustomerViewModel, TenantViewModel, UnitAccountViewModel, UserInputModel } from '@ail/customers-api'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../../../hooks/useAuth'
import { useGetAllCustomersQuery, useGetAllUnitsQuery } from '../../../../../redux/api/customerApi'

export function AssignCustomers(props: {
  editingUser: UserInputModel
  userTenant?: TenantViewModel
  onChange: (user: UserInputModel) => void
}) {
  const [assignedSearch, setAssignedSearch] = useState('')
  const [unassignedSearch, setUnassignedSearch] = useState('')
  const [expandedAssigned, setExpandedAssigned] = useState<Partial<Record<string, boolean>>>({})
  const [expandedUnassigned, setExpandedUnassigned] = useState<Partial<Record<string, boolean>>>({})
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const canAssign = user?.isAdmin || (user?.isInternalUser && user?.isManager)
  const { currentData: customers } = useGetAllCustomersQuery()
  const { currentData: units } = useGetAllUnitsQuery()

  const unitMaps = useMemo(() => {
    const byId: Partial<Record<string, UnitAccountViewModel>> = {}
    const byCustomerId: Partial<Record<string, UnitAccountViewModel[]>> = {}
    for (const unit of units ?? []) {
      const byCustomerIdEntry = byCustomerId[unit.unit.customerId] ?? []
      byId[unit.unit.id] = unit.unit
      byCustomerId[unit.unit.customerId] = byCustomerIdEntry
      byCustomerIdEntry.push(unit.unit)
    }
    return { byId, byCustomerId }
  }, [units])

  const assignedUnit = useCallback(
    (u: UnitAccountViewModel) => {
      return props.editingUser.customers?.includes(u.customerId) || props.editingUser.units?.includes(u.id)
    },
    [props.editingUser.customers, props.editingUser.units]
  )

  const assignedFilteredUnit = useCallback(
    (u: UnitAccountViewModel) => {
      return assignedUnit(u) && (!assignedSearch || u.name.toLowerCase().includes(assignedSearch.toLowerCase()))
    },
    [assignedSearch, assignedUnit]
  )

  const unassignedFilteredUnit = useCallback(
    (u: UnitAccountViewModel) => {
      return !assignedUnit(u) && (!unassignedSearch || u.name.toLowerCase().includes(unassignedSearch.toLowerCase()))
    },
    [assignedUnit, unassignedSearch]
  )

  const assignedCustomers = useMemo(() => {
    return customers?.filter(
      (c) =>
        props.editingUser.customers?.includes(c.customer.id) ||
        props.editingUser.units?.some((u) => unitMaps.byId[u]?.customerId === c.customer.id)
    )
  }, [customers, props.editingUser.customers, props.editingUser.units, unitMaps.byId])

  const assignedFilteredCustomers = useMemo(() => {
    return assignedCustomers?.filter(
      (c) =>
        !assignedSearch ||
        c.customer.name.toLowerCase().includes(assignedSearch.toLowerCase()) ||
        unitMaps.byCustomerId[c.customer.id]?.some((u) => assignedFilteredUnit(u))
    )
  }, [assignedCustomers, assignedFilteredUnit, assignedSearch, unitMaps.byCustomerId])

  const unassignedCustomers = useMemo(() => {
    return customers?.filter(
      (c) =>
        props.userTenant &&
        c.customer.tenantId === props.userTenant.id &&
        !props.editingUser.customers?.includes(c.customer.id) &&
        (!unitMaps.byCustomerId[c.customer.id]?.length ||
          unitMaps.byCustomerId[c.customer.id]?.some((u) => !props.editingUser.units?.includes(u.id)))
    )
  }, [customers, props.editingUser.customers, props.editingUser.units, props.userTenant, unitMaps.byCustomerId])

  const unassignedFilteredCustomers = useMemo(() => {
    return unassignedCustomers?.filter(
      (c) =>
        !unassignedSearch ||
        c.customer.name.toLowerCase().includes(unassignedSearch.toLowerCase()) ||
        unitMaps.byCustomerId[c.customer.id]?.some((u) => unassignedFilteredUnit(u))
    )
  }, [unassignedCustomers, unassignedFilteredUnit, unassignedSearch, unitMaps.byCustomerId])

  const assignCustomer = (customer: CustomerViewModel) => {
    const updatedUser: UserInputModel = { ...props.editingUser, tenants: [] }
    updatedUser.customers = [...(updatedUser.customers ?? []), customer.id]
    updatedUser.units = updatedUser.units?.filter((u) => unitMaps.byId[u]?.customerId !== customer.id)
    props.onChange(updatedUser)
  }

  const unassignCustomer = (customer: CustomerViewModel) => {
    const updatedUser: UserInputModel = { ...props.editingUser, tenants: [] }
    updatedUser.customers = updatedUser.customers?.filter((c) => c !== customer.id)
    updatedUser.units = updatedUser.units?.filter((u) => unitMaps.byId[u]?.customerId !== customer.id)
    props.onChange(updatedUser)
  }

  const assignUnit = (unit: UnitAccountViewModel) => {
    const updatedUser: UserInputModel = { ...props.editingUser, tenants: [] }
    updatedUser.units = [...(updatedUser.units ?? []), unit.id]
    const customerUnits = unitMaps.byCustomerId[unit.customerId]
    if (customerUnits?.every((u) => updatedUser.units?.includes(u.id))) {
      updatedUser.customers = [...(updatedUser.customers ?? []), unit.customerId]
      updatedUser.units = updatedUser.units?.filter((u) => unitMaps.byId[u]?.customerId !== unit.customerId)
    }
    props.onChange(updatedUser)
  }

  const unassignUnit = (unit: UnitAccountViewModel) => {
    const updatedUser: UserInputModel = { ...props.editingUser, tenants: [] }
    if (updatedUser.units?.includes(unit.id)) {
      updatedUser.units = updatedUser.units.filter((u) => u !== unit.id)
    } else if (updatedUser.customers?.includes(unit.customerId)) {
      updatedUser.customers = updatedUser.customers?.filter((c) => c !== unit.customerId)
      updatedUser.units = unitMaps.byCustomerId[unit.customerId]?.filter((u) => u.id !== unit.id).map((u) => u.id)
    }
    props.onChange(updatedUser)
  }

  return (
    <div className="box">
      <div className="itemTitle">
        <span>
          <i className={'icon-units'}></i>
          {t('UNITS')}
        </span>
      </div>
      <div className="row">
        <div className="col-6">
          <div className="box">
            <div className="unitsTitle">
              <label>{t('ASSIGNED_UNITS')}</label>
              <div className="input-area">
                <input
                  type="search"
                  placeholder={t('COST_CENTER_NAME_COMPANY_NAME')}
                  onChange={(e) => setAssignedSearch(e.target.value)}
                  value={assignedSearch}
                />
                {!!assignedSearch && <i className="icon-clear" onClick={() => setAssignedSearch('')} />}
                <i className="icon-search"></i>
              </div>
            </div>
            <div className="unitsListArea">
              {assignedFilteredCustomers?.map((customer) => (
                <>
                  <div className="compName">
                    <div className="custName">{customer.customer.name}</div>
                    <div>
                      <i className="icon-id"></i> <span>{customer.customer.reference}</span>
                      <i
                        className={expandedAssigned[customer.customer.id] ? 'icon-up' : 'icon-bottom'}
                        onClick={() =>
                          setExpandedAssigned({
                            ...expandedAssigned,
                            [customer.customer.id]: !expandedAssigned[customer.customer.id],
                          })
                        }
                      ></i>
                    </div>
                    {canAssign && <i className="icon-clear" onClick={() => unassignCustomer(customer.customer)}></i>}
                  </div>
                  {expandedAssigned[customer.customer.id] && (
                    <ul className={'unitsList'}>
                      {unitMaps.byCustomerId[customer.customer.id]?.filter(assignedFilteredUnit).map((unit) => (
                        <li>
                          <span>{unit.name || t('NO_COST_CENTER')}</span>
                          {canAssign && <i className="icon-clear" onClick={() => unassignUnit(unit)}></i>}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ))}
            </div>
          </div>
        </div>
        <div className="col-6 avaUnits">
          <div className="box">
            <div className="unitsTitle">
              <label>{t('SEARCH_UNITS')}</label>
              <div className="input-area">
                <input
                  type="search"
                  className="unit-search"
                  onChange={(e) => setUnassignedSearch(e.target.value)}
                  value={unassignedSearch}
                  placeholder={t('COST_CENTER_NAME_COMPANY_NAME')}
                />
                {!!unassignedSearch && <i className="icon-clear" onClick={() => setUnassignedSearch('')} />}
                <i className="icon-search"></i>
              </div>
            </div>
            <div className="unitsListArea">
              {unassignedFilteredCustomers?.map((customer) => (
                <>
                  <div className="compName">
                    <div className="custName">
                      {canAssign && <i className="icon-add" onClick={() => assignCustomer(customer.customer)}></i>}
                      {customer.customer.name}
                    </div>
                    <div>
                      <i className="icon-id"></i> <span>{customer.customer.reference}</span>
                      <i
                        className={expandedUnassigned[customer.customer.id] ? 'icon-up' : 'icon-bottom'}
                        onClick={() =>
                          setExpandedUnassigned({
                            ...expandedUnassigned,
                            [customer.customer.id]: !expandedUnassigned[customer.customer.id],
                          })
                        }
                      ></i>
                    </div>
                  </div>
                  {expandedUnassigned[customer.customer.id] && (
                    <ul className={'unitsList'}>
                      {unitMaps.byCustomerId[customer.customer.id]?.filter(unassignedFilteredUnit).map((unit) => (
                        <li>
                          <span>{unit.name || t('NO_COST_CENTER')}</span>
                          {canAssign && <i className="icon-add" onClick={() => assignUnit(unit)}></i>}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
